import { z } from 'zod';
import { idSchema, unitKeySchema } from './common.js';
import { seedEquipmentSchema } from './seed.js';
/** Recipe payloads (Phase 1). Routes live under /api/v1/recipes. */
export const recipeVisibilitySchema = z.enum(['private', 'public']);
export const recipeSourceKindSchema = z.enum([
    'family',
    'publix',
    'butcherbox',
    'other',
    'url_import',
]);
/** Attribution (D15/D19). Never carries household information (D12). */
export const recipeSourceSchema = z.object({
    kind: recipeSourceKindSchema,
    url: z.url().max(500).nullable().optional(),
    siteName: z.string().trim().max(100).nullable().optional(),
    author: z.string().trim().max(100).nullable().optional(),
    licence: z.string().trim().max(200).nullable().optional(),
});
export const recipeTagSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(1)
    .max(40)
    .regex(/^[a-z0-9][a-z0-9 _-]*$/, 'letters, digits, spaces, - or _');
/**
 * Controlled vocabularies (D26). Keys are stored; labels are for display.
 * Untagged recipes count as mains for the week planner.
 */
export const CUISINES = [
    'american',
    'bbq',
    'southern',
    'cajun',
    'tex-mex',
    'mexican',
    'latin-american',
    'caribbean',
    'italian',
    'french',
    'spanish',
    'mediterranean',
    'greek',
    'middle-eastern',
    'indian',
    'asian',
    'chinese',
    'japanese',
    'korean',
    'thai',
    'vietnamese',
    'hawaiian',
    'fusion',
    'other',
];
export const cuisineSchema = z.enum(CUISINES);
export const CUISINE_LABELS = {
    american: 'American',
    bbq: 'BBQ',
    southern: 'Southern',
    cajun: 'Cajun & Creole',
    'tex-mex': 'Tex-Mex',
    mexican: 'Mexican',
    'latin-american': 'Latin American',
    caribbean: 'Caribbean',
    italian: 'Italian',
    french: 'French',
    spanish: 'Spanish',
    mediterranean: 'Mediterranean',
    greek: 'Greek',
    'middle-eastern': 'Middle Eastern',
    indian: 'Indian',
    asian: 'Asian',
    chinese: 'Chinese',
    japanese: 'Japanese',
    korean: 'Korean',
    thai: 'Thai',
    vietnamese: 'Vietnamese',
    hawaiian: 'Hawaiian',
    fusion: 'Fusion',
    other: 'Other',
};
export const COURSES = [
    'main',
    'side',
    'appetizer',
    'soup',
    'salad',
    'bread',
    'sauce',
    'breakfast',
    'dessert',
    'snack',
    'drink',
];
export const courseSchema = z.enum(COURSES);
export const COURSE_LABELS = {
    main: 'Entrée',
    side: 'Side',
    appetizer: 'Appetizer',
    soup: 'Soup',
    salad: 'Salad',
    bread: 'Bread',
    sauce: 'Sauce',
    breakfast: 'Breakfast',
    dessert: 'Dessert',
    snack: 'Snack',
    drink: 'Drink',
};
export const stepEquipmentSchema = seedEquipmentSchema;
/** One ingredient line on input: reference an existing ingredient or give a free-text name. */
export const recipeIngredientInputSchema = z
    .object({
    ingredientId: idSchema.optional(),
    name: z.string().trim().min(1).max(120).optional(),
    qty: z.number().positive().nullable().default(null),
    qtyMax: z.number().positive().nullable().optional(),
    unit: unitKeySchema.default('count'),
    prepNote: z.string().trim().max(200).nullable().optional(),
    optional: z.boolean().default(false),
    /** Verbatim source line, kept for review/re-parse. */
    raw: z.string().max(400).nullable().optional(),
})
    .refine((v) => Boolean(v.ingredientId || v.name), {
    message: 'ingredientId or name is required',
    path: ['name'],
});
/**
 * One recipe step on create/update. `timer` defaults from the duration
 * (PLAN §9 #4): when the client omits `timer`, a step with `durationMin > 0`
 * gets `timer: true`, otherwise `false`. An explicit `timer: false` always
 * sticks. (The `recipe_step.timer` column default stays `false`.)
 */
export const recipeStepInputSchema = z
    .object({
    text: z.string().trim().min(1).max(2000),
    durationMin: z.number().positive().max(10_080).nullable().default(null),
    attended: z.boolean().default(true),
    timer: z.boolean().optional(),
    canDoAhead: z.boolean().default(false),
    equipment: stepEquipmentSchema.nullable().optional(),
    /** Indexes into the request's `ingredients` array; resolved to ingredient ids server-side. */
    ingredientRefs: z.array(z.number().int().nonnegative()).max(50).default([]),
})
    .transform(({ timer, ...rest }) => ({
    ...rest,
    timer: timer ?? (rest.durationMin !== null && rest.durationMin > 0),
}));
const titleSchema = z.string().trim().min(1).max(200);
const descriptionSchema = z.string().trim().max(1000).nullable();
const servingsSchema = z.number().int().positive().max(200).nullable();
const minutesSchema = z.number().int().nonnegative().max(10_080).nullable();
/** Minutes the finished dish rests/holds before serving; the timeline ends cooking that much before serveAt. */
const restMinSchema = z.number().int().nonnegative().max(1440);
const tagsSchema = z.array(recipeTagSchema).max(20);
const cuisinesSchema = z.array(cuisineSchema).max(4);
const coursesSchema = z.array(courseSchema).max(4);
/** POST /api/v1/recipes */
export const createRecipeRequestSchema = z.object({
    title: titleSchema,
    description: descriptionSchema.default(null),
    servings: servingsSchema.default(null),
    prepMin: minutesSchema.default(null),
    cookMin: minutesSchema.default(null),
    restMin: restMinSchema.default(0),
    visibility: recipeVisibilitySchema.default('private'),
    tags: tagsSchema.default([]),
    cuisines: cuisinesSchema.default([]),
    courses: coursesSchema.default([]),
    ingredients: z.array(recipeIngredientInputSchema).max(100).default([]),
    steps: z.array(recipeStepInputSchema).max(100).default([]),
    source: recipeSourceSchema.nullable().optional(),
});
/** PATCH /api/v1/recipes/:id — partial; `ingredients`/`steps` replace wholesale when present. */
export const updateRecipeRequestSchema = z.object({
    title: titleSchema.optional(),
    description: descriptionSchema.optional(),
    servings: servingsSchema.optional(),
    prepMin: minutesSchema.optional(),
    cookMin: minutesSchema.optional(),
    restMin: restMinSchema.optional(),
    visibility: recipeVisibilitySchema.optional(),
    tags: tagsSchema.optional(),
    cuisines: cuisinesSchema.optional(),
    courses: coursesSchema.optional(),
    ingredients: z.array(recipeIngredientInputSchema).max(100).optional(),
    steps: z.array(recipeStepInputSchema).max(100).optional(),
    source: recipeSourceSchema.nullable().optional(),
    /** Must be a media row owned by the household (uploaded through /api/v1/media). */
    photoMediaId: idSchema.nullable().optional(),
});
export const recipeSortSchema = z.enum(['updated', 'title', 'rating']);
/** GET /api/v1/recipes and GET /api/v1/recipes/public */
export const recipeListQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    tag: recipeTagSchema.optional(),
    cuisine: cuisineSchema.optional(),
    course: courseSchema.optional(),
    visibility: recipeVisibilitySchema.optional(),
    sort: recipeSortSchema.default('updated'),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const recipeRatingSummarySchema = z.object({
    avg: z.number().nullable(),
    count: z.number().int(),
});
export const recipeSummarySchema = z.object({
    id: idSchema,
    slug: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    photoMediaId: idSchema.nullable(),
    servings: z.number().int().nullable(),
    prepMin: z.number().int().nullable(),
    cookMin: z.number().int().nullable(),
    /** Rest/hold minutes before serving (default keeps older cached payloads parseable). */
    restMin: z.number().int().nonnegative().default(0),
    visibility: recipeVisibilitySchema,
    tags: z.array(z.string()),
    cuisines: z.array(cuisineSchema),
    courses: z.array(courseSchema),
    rating: recipeRatingSummarySchema,
    forkedFromRecipeId: idSchema.nullable(),
    source: recipeSourceSchema.nullable(),
    /** True when the recipe belongs to the caller's household. */
    own: z.boolean(),
    /**
     * Hidden from the public library after reports, pending admin review.
     * Only ever true for the caller's own recipe (others cannot see it).
     */
    hidden: z.boolean().default(false),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const recipeIngredientDtoSchema = z.object({
    id: idSchema,
    ingredientId: idSchema,
    /** Canonical ingredient name from the shared catalog. */
    name: z.string(),
    qty: z.number().nullable(),
    qtyMax: z.number().nullable(),
    unit: unitKeySchema,
    prepNote: z.string().nullable(),
    optional: z.boolean(),
    sort: z.number().int(),
    raw: z.string().nullable(),
});
export const recipeStepDtoSchema = z.object({
    id: idSchema,
    sort: z.number().int(),
    text: z.string(),
    durationMin: z.number().nullable(),
    attended: z.boolean(),
    timer: z.boolean(),
    canDoAhead: z.boolean(),
    equipment: stepEquipmentSchema.nullable(),
    ingredientRefs: z.array(idSchema),
});
export const myRatingSchema = z.object({
    stars: z.number().int().min(1).max(5),
    notes: z.string().nullable(),
});
/** One numbered note, visible only to the household that wrote it (D26/D12). */
export const recipeNoteDtoSchema = z.object({
    id: idSchema,
    /** 1-based display number, dense per recipe within the household. */
    number: z.number().int().positive(),
    text: z.string(),
    /** Display name of the member who wrote it; null once that account is gone. */
    authorName: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/**
 * One person of the caller's household (account member or not, D30) and
 * their rating; stars null when nobody has rated for them yet.
 */
export const personRatingSchema = z.object({
    personId: idSchema,
    name: z.string(),
    hasAccount: z.boolean(),
    /** True for the signed-in caller's own person. */
    isMe: z.boolean(),
    stars: z.number().int().min(1).max(5).nullable(),
    notes: z.string().nullable(),
    /**
     * Who entered the rating when it was not the person themselves (always set
     * for people without an account); null when self-rated or the rater's
     * account is gone.
     */
    ratedByName: z.string().nullable(),
    updatedAt: z.iso.datetime().nullable(),
});
export const recipeDetailSchema = recipeSummarySchema.extend({
    ingredients: z.array(recipeIngredientDtoSchema),
    steps: z.array(recipeStepDtoSchema),
    myRating: myRatingSchema.nullable(),
    /** The caller's household's own notes, in display order. */
    notes: z.array(recipeNoteDtoSchema),
    /** Every person of the caller's household (D30), rated or not. */
    personRatings: z.array(personRatingSchema),
    /** Average over the caller's household people only (`rating` is the global aggregate). */
    householdRating: recipeRatingSummarySchema,
});
export const recipeListResponseSchema = z.object({
    recipes: z.array(recipeSummarySchema),
    total: z.number().int(),
});
/**
 * PUT /api/v1/recipes/:id/rating (the caller's own person) and
 * PUT /api/v1/recipes/:id/ratings/:personId (anyone in the household, D30).
 */
export const rateRecipeRequestSchema = z.object({
    stars: z.number().int().min(1).max(5),
    notes: z.string().trim().max(1000).nullable().optional(),
});
export const recipeTagsResponseSchema = z.object({
    tags: z.array(z.object({ tag: z.string(), count: z.number().int() })),
});
export const recipeIdParamsSchema = z.object({ id: idSchema });
export const recipePersonParamsSchema = z.object({ id: idSchema, personId: idSchema });
// ----------------------------------------------------------- notes (D26) ---
const noteTextSchema = z.string().trim().min(1).max(1000);
/** POST /api/v1/recipes/:id/notes and PATCH /api/v1/recipes/:id/notes/:noteId */
export const recipeNoteRequestSchema = z.object({ text: noteTextSchema });
/** PUT /api/v1/recipes/:id/notes/order — every note id of the household, in the new order. */
export const reorderRecipeNotesRequestSchema = z.object({
    noteIds: z.array(idSchema).max(200),
});
export const recipeNotesResponseSchema = z.object({ notes: z.array(recipeNoteDtoSchema) });
export const recipeNoteParamsSchema = z.object({ id: idSchema, noteId: idSchema });
// --------------------------------------------------------- rankings (D26) ---
/** GET /api/v1/recipes/rankings — `sortBy` is `household` or a household person id (D30). */
export const recipeRankingsQuerySchema = z.object({
    cuisine: cuisineSchema.optional(),
    course: courseSchema.optional(),
    sortBy: z.union([z.literal('household'), idSchema]).default('household'),
    limit: z.coerce.number().int().min(1).max(200).default(100),
    offset: z.coerce.number().int().min(0).default(0),
});
export const recipeRankingRowSchema = z.object({
    recipe: recipeSummarySchema,
    householdAvg: z.number().nullable(),
    ratedCount: z.number().int(),
    /** Keyed by person id; null when nobody has rated it for that person. */
    stars: z.record(z.string(), z.number().int().min(1).max(5).nullable()),
});
export const recipeRankingsResponseSchema = z.object({
    people: z.array(z.object({
        personId: idSchema,
        name: z.string(),
        hasAccount: z.boolean(),
        isMe: z.boolean(),
    })),
    recipes: z.array(recipeRankingRowSchema),
    total: z.number().int(),
});
//# sourceMappingURL=recipes.js.map